import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Clock, 
  Zap, 
  Mail, 
  Calendar, 
  FileText, 
  Database,
  Share2,
  ChevronDown,
  ChevronUp,
  Loader2
} from 'lucide-react';
import apiClient from '../utils/apiClient';
import { toast } from 'sonner';

/**
 * ActionApprovalCard - Shows proposed actions for user approval
 * 
 * Used when AI skills propose actions that need explicit user approval
 * based on the approvalMode setting (draft/assisted/auto)
 */
export default function ActionApprovalCard({ 
  actionEnvelope, 
  messageId, 
  threadId,
  onActionProcessed 
}) {
  const [expandedActions, setExpandedActions] = useState({});
  const [processingAction, setProcessingAction] = useState(null);
  const [actionStatuses, setActionStatuses] = useState({});
  const [rejectionReason, setRejectionReason] = useState({});
  const [showRejectInput, setShowRejectInput] = useState({});

  if (!actionEnvelope || !actionEnvelope.proposedActions?.length) {
    return null;
  }

  const { proposedActions, approvalMode, installationId, executedActions = [] } = actionEnvelope;

  const getActionIcon = (actionType) => {
    const icons = {
      'send_email': Mail,
      'create_task': CheckCircle,
      'schedule_meeting': Calendar,
      'generate_document': FileText,
      'update_record': Database,
      'post_content': Share2,
      'analyze_data': Zap,
      'other': AlertTriangle
    };
    return icons[actionType] || AlertTriangle;
  };

  const getRiskBadgeColor = (riskLevel) => {
    switch (riskLevel) {
      case 'low': return 'bg-green-100 text-green-700 border-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getApprovalModeBadge = () => {
    switch (approvalMode) {
      case 'draft':
        return <Badge className="bg-gray-100 text-gray-700 border-gray-200">Draft Mode</Badge>;
      case 'assisted':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Approval Required</Badge>;
      case 'auto':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Auto Mode</Badge>;
      default:
        return null;
    }
  };

  const toggleExpand = (actionId) => {
    setExpandedActions(prev => ({
      ...prev,
      [actionId]: !prev[actionId]
    }));
  };

  const handleApprove = async (action) => {
    try {
      setProcessingAction(action.actionId);
      
      const response = await apiClient.post('/skills/actions/approve', {
        threadId,
        messageId,
        installationId,
        actionId: action.actionId
      });

      setActionStatuses(prev => ({
        ...prev,
        [action.actionId]: 'executed'
      }));

      toast.success('Action approved and executed successfully');
      
      if (onActionProcessed) {
        onActionProcessed(action.actionId, 'executed', response.data);
      }
    } catch (error) {
      console.error('Failed to approve action:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve action');
      setActionStatuses(prev => ({
        ...prev,
        [action.actionId]: 'failed'
      }));
    } finally {
      setProcessingAction(null);
    }
  };

  const handleReject = async (action) => {
    try {
      setProcessingAction(action.actionId);
      
      const response = await apiClient.post('/skills/actions/reject', {
        threadId,
        messageId,
        installationId,
        actionId: action.actionId,
        reason: rejectionReason[action.actionId] || null
      });

      setActionStatuses(prev => ({
        ...prev,
        [action.actionId]: 'rejected'
      }));

      toast.success('Action rejected');
      setShowRejectInput(prev => ({ ...prev, [action.actionId]: false }));
      
      if (onActionProcessed) {
        onActionProcessed(action.actionId, 'rejected', response.data);
      }
    } catch (error) {
      console.error('Failed to reject action:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject action');
    } finally {
      setProcessingAction(null);
    }
  };

  const isActionProcessed = (actionId) => {
    return actionStatuses[actionId] || executedActions.includes(actionId);
  };

  const getActionStatus = (action) => {
    if (executedActions.includes(action.actionId)) return 'auto_executed';
    if (action.status) return action.status;
    return actionStatuses[action.actionId] || 'pending';
  };

  return (
    <div className="mt-3 ml-11 space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-500" />
        <span className="text-sm font-medium text-gray-700">
          {approvalMode === 'draft' ? 'Proposed Actions (Draft Only)' : 'Actions Requiring Approval'}
        </span>
        {getApprovalModeBadge()}
      </div>

      {/* Actions List */}
      <div className="space-y-2">
        {proposedActions.map((action) => {
          const ActionIcon = getActionIcon(action.actionType);
          const status = getActionStatus(action);
          const isProcessed = status !== 'pending';
          const isExpanded = expandedActions[action.actionId];
          const isProcessing = processingAction === action.actionId;

          return (
            <div 
              key={action.actionId}
              className={`border rounded-lg p-3 ${
                isProcessed 
                  ? status === 'executed' || status === 'auto_executed'
                    ? 'bg-green-50 border-green-200'
                    : status === 'rejected'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-gray-50 border-gray-200'
                  : 'bg-white border-gray-200'
              }`}
            >
              {/* Action Header */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    isProcessed 
                      ? status === 'executed' || status === 'auto_executed'
                        ? 'bg-green-100'
                        : 'bg-red-100'
                      : 'bg-amber-100'
                  }`}>
                    <ActionIcon className={`w-4 h-4 ${
                      isProcessed
                        ? status === 'executed' || status === 'auto_executed'
                          ? 'text-green-600'
                          : 'text-red-600'
                        : 'text-amber-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{action.title}</span>
                      <Badge className={`text-xs ${getRiskBadgeColor(action.riskLevel)}`}>
                        {action.riskLevel} risk
                      </Badge>
                      {status === 'auto_executed' && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          Auto-executed
                        </Badge>
                      )}
                      {status === 'executed' && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Executed
                        </Badge>
                      )}
                      {status === 'rejected' && (
                        <Badge className="bg-red-100 text-red-700 text-xs">
                          <XCircle className="w-3 h-3 mr-1" />
                          Rejected
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{action.description}</p>
                  </div>
                </div>

                {/* Expand/Collapse */}
                <button
                  onClick={() => toggleExpand(action.actionId)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="text-xs text-gray-600 space-y-1">
                    <p><strong>Action Type:</strong> {action.actionType.replace('_', ' ')}</p>
                    {action.estimatedImpact && (
                      <p><strong>Impact:</strong> {action.estimatedImpact}</p>
                    )}
                    <p><strong>Reversible:</strong> {action.reversible ? 'Yes' : 'No'}</p>
                    {Object.keys(action.payload || {}).length > 0 && (
                      <div>
                        <strong>Payload:</strong>
                        <pre className="mt-1 p-2 bg-gray-50 rounded text-xs overflow-auto max-h-24">
                          {JSON.stringify(action.payload, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons - Only show for pending actions */}
              {!isProcessed && approvalMode !== 'draft' && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  {showRejectInput[action.actionId] ? (
                    <div className="space-y-2">
                      <Textarea
                        placeholder="Reason for rejection (optional)"
                        value={rejectionReason[action.actionId] || ''}
                        onChange={(e) => setRejectionReason(prev => ({
                          ...prev,
                          [action.actionId]: e.target.value
                        }))}
                        className="text-xs min-h-[60px]"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleReject(action)}
                          disabled={isProcessing}
                          className="h-7 px-3 text-xs"
                        >
                          {isProcessing ? (
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          ) : (
                            <XCircle className="w-3 h-3 mr-1" />
                          )}
                          Confirm Reject
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowRejectInput(prev => ({
                            ...prev,
                            [action.actionId]: false
                          }))}
                          className="h-7 px-3 text-xs"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleApprove(action)}
                        disabled={isProcessing}
                        className="h-7 px-3 text-xs bg-green-600 hover:bg-green-700"
                      >
                        {isProcessing ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <CheckCircle className="w-3 h-3 mr-1" />
                        )}
                        Approve & Execute
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowRejectInput(prev => ({
                          ...prev,
                          [action.actionId]: true
                        }))}
                        disabled={isProcessing}
                        className="h-7 px-3 text-xs border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <XCircle className="w-3 h-3 mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Draft mode message */}
              {!isProcessed && approvalMode === 'draft' && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    <span>Draft mode - Actions cannot be executed. Switch to Assisted or Auto mode to enable execution.</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
